import {
  Activity,
  Brain,
  FlaskConical,
  LayoutDashboard,
  LineChart,
  Sliders,
  Smile,
  Sparkles,
  Target,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Badge, PageLayout, Select, Tooltip } from "../components";
import AILab from "../components/analytics/AILab";
import ContentRecommendations from "../components/analytics/ContentRecommendations";
import IntelligentInsights from "../components/analytics/IntelligentInsights";
import RealTimeDashboard from "../components/analytics/RealTimeDashboard";
import SentimentAnalysis from "../components/analytics/SentimentAnalysis";
import {
  AILabSkeleton,
  ContentRecommendationsSkeleton,
  IntelligentInsightsSkeleton,
  RealTimeDashboardSkeleton,
  SentimentAnalysisSkeleton,
  TrendAnalysisSkeleton,
} from "../components/analytics/skeletons";
import TrendAnalysis from "../components/analytics/TrendAnalysis";
import AdvancedAnalyticsDashboard from "../components/dashboards/AdvancedAnalyticsDashboard";
import LeadAttributionDashboard from "../components/dashboards/LeadAttributionDashboard";
import analyticsService from "../services/analyticsService";
import { DashboardData } from "../types/analytics";
import { cn } from "../utils/cn";

import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";

// 8 abas → 6: "Horários Ótimos" e "CTAs de Conversão" foram mescladas em
// "contentRecommendations" (mesma requisição de API, chaves diferentes do
// mesmo payload — ver ContentRecommendations.tsx); "Predições" e "Hashtags"
// foram mescladas em "aiLab" (mesmo padrão de interação: formulário →
// simulação sob demanda — ver AILab.tsx).
type TabId =
  | "intelligent"
  | "sentiment"
  | "trends"
  | "dashboard"
  | "contentRecommendations"
  | "aiLab"
  | "advanced"
  | "attribution";

const VALID_TABS: TabId[] = [
  "dashboard",
  "intelligent",
  "trends",
  "sentiment",
  "contentRecommendations",
  "aiLab",
  "advanced",
  "attribution",
];

const Analytics: React.FC = () => {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const fromUrl = searchParams.get("tab") as TabId | null;
    return fromUrl && VALID_TABS.includes(fromUrl) ? fromUrl : "dashboard";
  });
  const [period, setPeriod] = useState("30");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [approvedPosts, setApprovedPosts] = useState<number | null>(null);

  // Track which tabs have ever been opened so they stay mounted (avoids re-fetch on back-nav)
  const [mountedTabs, setMountedTabs] = useState<Set<TabId>>(
    new Set(["dashboard", activeTab]),
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", activeTab);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const PERIOD_OPTIONS = [
    { value: "7", label: t("7 dias") },
    { value: "30", label: t("30 dias") },
    { value: "90", label: t("90 dias") },
  ];

  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const prevContextIdRef = useRef(contextId);

  const extractApprovedPosts = (data: any): number => {
    const candidates = [
      data?.metrics?.total_posts,
      data?.data?.metrics?.total_posts,
      data?.data?.summary?.published_posts,
      data?.data?.summary?.total_posts,
      data?.summary?.published_posts,
      data?.summary?.total_posts,
    ];
    const numeric = candidates.find(
      (v) => typeof v === "number" && !Number.isNaN(v),
    );
    return typeof numeric === "number" ? numeric : 0;
  };

  const analyzePerformance = useCallback(async () => {
    setLoading(true);
    try {
      const data = await analyticsService.getPerformanceDashboard();
      setAnalytics(data);
      setApprovedPosts(extractApprovedPosts(data));
      setErrorMessage("");
    } catch (error) {
      console.error("Erro ao analisar performance:", error);
      setErrorMessage(
        t("Não foi possível carregar os dados de analytics. Tente novamente."),
      );
      setAnalytics(null);
      // Keep approvedPosts as null on error so we don't misinterpret absence of
      // data as "zero posts" — which would falsely trigger the learning-mode banner.
      setApprovedPosts(null);
    } finally {
      setLoading(false);
    }
  }, [
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
    period,
    t,
  ]);

  // Reset state when context changes, using a ref to avoid setState during render
  useEffect(() => {
    if (prevContextIdRef.current !== contextId) {
      prevContextIdRef.current = contextId;
      setAnalytics(null);
      setApprovedPosts(null);
      setMountedTabs(new Set(["dashboard"]));
    }
  }, [contextId]);

  useEffect(() => {
    analyzePerformance();
  }, [analyzePerformance]);

  const handleTabChange = (tabId: string) => {
    const id = tabId as TabId;
    setActiveTab(id);
    setMountedTabs((prev) => new Set([...prev, id]));
  };

  // Ordem pensada como um fluxo de análise: visão geral em tempo real →
  // diagnóstico consolidado da IA → tendências/previsões → comportamento da
  // audiência → recomendações específicas (horários + CTAs, mescladas) →
  // laboratório de simulação sob demanda (Predições + Hashtags, mescladas),
  // por último por ser a ferramenta mais "manual"/exploratória, não um
  // painel de leitura direta.
  const tabs = [
    {
      id: "dashboard",
      label: (
        <span className="flex items-center gap-1.5">
          <LayoutDashboard className="h-4 w-4" />
          {t("Dashboard")}
          {autoRefresh && (
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          )}
        </span>
      ) as any,
      icon: null,
    },
    {
      id: "intelligent",
      label: t("Insights"),
      icon: <Brain className="h-4 w-4" />,
    },
    {
      id: "trends",
      label: t("Tendências"),
      icon: <LineChart className="h-4 w-4" />,
    },
    {
      id: "sentiment",
      label: t("Sentimento"),
      icon: <Smile className="h-4 w-4" />,
    },
    {
      id: "contentRecommendations",
      label: t("Recomendações"),
      icon: <Sliders className="h-4 w-4" />,
    },
    {
      id: "aiLab",
      label: t("Laboratório de IA"),
      icon: <Sparkles className="h-4 w-4" />,
    },
    {
      id: "advanced",
      label: t("Testes A/B"),
      icon: <FlaskConical className="h-4 w-4" />,
    },
    {
      id: "attribution",
      label: t("Atribuição de Leads"),
      icon: <Target className="h-4 w-4" />,
    },
  ];

  const skeletonForTab = (tab: TabId) => {
    switch (tab) {
      case "intelligent":
        return <IntelligentInsightsSkeleton />;
      case "trends":
        return <TrendAnalysisSkeleton />;
      case "sentiment":
        return <SentimentAnalysisSkeleton />;
      case "dashboard":
        return <RealTimeDashboardSkeleton />;
      case "contentRecommendations":
        return <ContentRecommendationsSkeleton />;
      case "aiLab":
        return <AILabSkeleton />;
      case "advanced":
        return <RealTimeDashboardSkeleton />;
      case "attribution":
        return <RealTimeDashboardSkeleton />;
    }
  };

  return (
    <PageLayout
      title={t("Analytics & IA")}
      subtitle={t(
        "Análise profunda e inteligência preditiva para sua estratégia de conteúdo social.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={loading}
      skeleton={
        <div className="animate-in fade-in duration-500 mt-2">
          {skeletonForTab(activeTab)}
        </div>
      }
      actions={
        <div className="flex items-center gap-3">
          <Select
            value={period}
            onChange={setPeriod}
            options={PERIOD_OPTIONS}
            size="sm"
            className="min-w-[110px] h-8! py-0! rounded-full! text-xs"
          />

          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50/80 backdrop-blur-xs border border-emerald-100/50 rounded-full shadow-xs">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
            <span className="text-[9px] font-black text-emerald-700 uppercase tracking-[0.2em] leading-none">
              {t("IA Ativa")}
            </span>
          </div>

          <Tooltip content={t("Sincronizar Dados")} position="top">
            <button
              onClick={analyzePerformance}
              disabled={loading}
              aria-label={t("Sincronizar Dados")}
              className="p-2 text-purple-600 bg-purple-50/50 hover:bg-purple-100 rounded-full transition-all group border border-purple-100/50 shadow-xs disabled:opacity-50"
            >
              <Activity size={14} className={cn(loading && "animate-spin")} />
            </button>
          </Tooltip>
          <Badge
            variant="secondary"
            className="bg-purple-100/50 text-purple-700 border-purple-200 rounded-full font-black uppercase text-[8px] tracking-widest px-2.5 py-0.5 shadow-xs"
          >
            {t("Beta")}
          </Badge>
        </div>
      }
    >
      {/* Navigation Tabs — mesmo controle segmentado de AutoResponder.tsx e
          EmpathyMapping.tsx (preenchimento sólido no item ativo, sem
          depender de nuance de cor). O componente compartilhado `Tabs`
          (variant="pills") força py-2.5 mesmo com size="sm" e adiciona
          padding/arredondamento que esse padrão não tem — daí a divergência
          de UI/UX com as outras páginas de nível superior com abas. */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 bg-white/95 backdrop-blur-md border-b border-purple-200">
        <div className="w-full flex gap-1 bg-gray-100 p-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-bold uppercase tracking-wide transition-all duration-150",
                  isActive
                    ? "bg-purple-600 text-white shadow-xs"
                    : "bg-white text-gray-600 border border-gray-200 shadow-xs hover:text-purple-700 hover:border-purple-300 cursor-pointer",
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="w-full mt-2 flex flex-col gap-2">
        {errorMessage && (
          <Alert variant="warning" className="border-rose-100 bg-rose-50/30">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs font-black uppercase tracking-widest text-rose-600">
                {errorMessage}
              </p>
              <button
                onClick={analyzePerformance}
                className="text-[10px] font-black uppercase tracking-widest px-4 py-2 bg-rose-100 text-rose-600 rounded-lg hover:bg-rose-200"
              >
                {t("Reprocessar")}
              </button>
            </div>
          </Alert>
        )}

        {approvedPosts !== null &&
          approvedPosts < 10 &&
          activeTab === "aiLab" && (
            <Alert
              variant="warning"
              icon={<Activity className="h-5 w-5" />}
              title={t("Modo de Aprendizado")}
            >
              {t(
                "A IA precisa de pelo menos 10 posts publicados para calibrar as predições de acordo com seu perfil. Atualmente exibindo resultados baseados em benchmarks globais.",
              )}
            </Alert>
          )}

        {/* Tabs are kept mounted once visited to avoid re-fetching on back-navigation.
            Sem min-h fixo: cada aba tem seu próprio conteúdo/altura natural. */}
        <div className="animate-in fade-in duration-500">
          {mountedTabs.has("intelligent") && (
            <div className={activeTab !== "intelligent" ? "hidden" : ""}>
              <IntelligentInsights />
            </div>
          )}
          {mountedTabs.has("trends") && (
            <div className={activeTab !== "trends" ? "hidden" : ""}>
              <TrendAnalysis />
            </div>
          )}
          {mountedTabs.has("sentiment") && (
            <div className={activeTab !== "sentiment" ? "hidden" : ""}>
              <SentimentAnalysis />
            </div>
          )}
          {mountedTabs.has("dashboard") && (
            <div className={activeTab !== "dashboard" ? "hidden" : ""}>
              <RealTimeDashboard
                initialData={analytics}
                autoRefresh={autoRefresh}
                onAutoRefreshChange={setAutoRefresh}
              />
            </div>
          )}
          {mountedTabs.has("contentRecommendations") && (
            <div
              className={activeTab !== "contentRecommendations" ? "hidden" : ""}
            >
              <ContentRecommendations days={Number(period)} />
            </div>
          )}
          {mountedTabs.has("advanced") && (
            <div className={activeTab !== "advanced" ? "hidden" : ""}>
              <AdvancedAnalyticsDashboard />
            </div>
          )}
          {mountedTabs.has("attribution") && (
            <div className={activeTab !== "attribution" ? "hidden" : ""}>
              <LeadAttributionDashboard />
            </div>
          )}
          {mountedTabs.has("aiLab") && (
            <div className={activeTab !== "aiLab" ? "hidden" : ""}>
              <AILab />
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Analytics;
